import { memo, useEffect } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { isEmpty } from 'lodash-es';
import { useSelector, useDispatch } from 'react-redux';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { clearRichFields } from 'features/srPanel/templateFieldsGrid/richTextField/store/slice';
import { selectPanels } from 'store/floatingTicketPanelSlice';
import { selectFilterModel, selectRememberRow, selectColumnsOrder } from './slice';
import ColumnsConfiguration from './grid/columnsConfiguration';
import Grid from './grid';
import GridFilters from './gridFilters';
import GridConfigurationHeader from './gridConfigurationHeader';
import { StyledQueue, StyledContainer } from './style';
import useColumnTexts from './grid/customColumnHeader/useTexts';
import { useGetColumnDefinitions } from './grid/hooks/useGetColumnDefinitions';
import { selectDirection } from '../../store/globalSlice';
import { useContextQuery } from '../../remote-state/platformIntegrationServiceHooks';
import FeatureFlagMonitor from '../../FeatureFlagMonitor';

const queueSelector = createSelector(
  selectFilterModel,
  selectRememberRow,
  selectColumnsOrder,
  (filterModel, { scrollToIndex, scrollToId }, columnsOrder) => ({
    filterModel,
    scrollToIndex,
    scrollToId,
    columnsOrder,
  }),
);
const Queue = memo(() => {
  const dispatch = useDispatch();
  const { getColumnText } = useColumnTexts();
  const { filterModel, scrollToIndex, scrollToId, columnsOrder } = useSelector(queueSelector);
  const panels = useSelector(selectPanels);
  const selectDir = useSelector(selectDirection);
  const configuredColumnsList = useGetColumnDefinitions(columnsOrder, getColumnText, selectDir);
  useEffect(() => {
    const cleanUp = () => {
      dispatch(clearRichFields());
    };
    return () => cleanUp();
  }, [dispatch]);

  const ldClient = useLDClient();
  const { data: context= undefined } = useContextQuery();

  useEffect(() => {
    if (context) ldClient.identify({key: context?.account, ...context}).catch((error) => {
      console.error('Failed to identify context with LaunchDarkly:', error);
    });
  }, [ldClient, context]);
  return (
    <StyledQueue isPanelsOpen={Object.keys(panels).length > 0}>
      {process.env.NODE_ENV !== 'development' && <FeatureFlagMonitor />}
      <StyledContainer>
        <GridConfigurationHeader className="header" />
      </StyledContainer>
      {columnsOrder?.length > 0 && (
        <>
          <GridFilters />
          <ColumnsConfiguration />
          <Grid
            className="content"
            enforceFilterModel={filterModel}
            scrollToIndex={scrollToIndex}
            scrollToId={scrollToId}
            showFilters={!isEmpty(filterModel)}
            columnDefs={configuredColumnsList}
          />
        </>
      )}
    </StyledQueue>
  );
});

export default Queue;
